// Account endpoints (04 §4.1–§4.2, slice subset: no OAuth, phone, push devices or store fields).
import { z } from 'zod';
import { accountRoleSchema, idSchema } from './common.js';

/** Technical input limits (not balance). */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;

export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));
const newPasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
const tokenSchema = z.string().min(1).max(256);

export const registerRequestSchema = z.strictObject({
  email: emailSchema,
  password: newPasswordSchema,
  /** Required only when the server has a registration code (tester environment). */
  inviteCode: z.string().max(64).optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const registrationInfoSchema = z.strictObject({ inviteCodeRequired: z.boolean() });
export type RegistrationInfo = z.infer<typeof registrationInfoSchema>;

export const loginRequestSchema = z.strictObject({
  email: emailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const accountSchema = z.strictObject({
  id: idSchema,
  email: z.string(),
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  role: accountRoleSchema,
  /** Account-wide wallet (G-28). */
  darkMatter: z.number().int().nonnegative(),
});
export type Account = z.infer<typeof accountSchema>;

/** `refreshToken` is in the body only for Capacitor clients; web clients get the cookie. */
export const authResponseSchema = z.strictObject({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  account: accountSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const verifyEmailRequestSchema = z.strictObject({ token: tokenSchema });
export const verifyEmailResponseSchema = z.strictObject({ emailVerified: z.literal(true) });

export const refreshRequestSchema = z.strictObject({ refreshToken: tokenSchema.optional() });
export const refreshResponseSchema = z.strictObject({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
});

export const logoutRequestSchema = z.strictObject({ allDevices: z.boolean().optional() });
export const logoutResponseSchema = z.strictObject({ loggedOut: z.literal(true) });

export const passwordForgotRequestSchema = z.strictObject({ email: emailSchema });
export const passwordResetRequestSchema = z.strictObject({
  token: tokenSchema,
  newPassword: newPasswordSchema,
});

export const wsTicketRequestSchema = z.strictObject({ galaxyId: idSchema });
export const wsTicketResponseSchema = z.strictObject({
  ticket: z.string().startsWith('wst_'),
  expiresInSeconds: z.number().int().positive(),
});

export const meResponseSchema = z.strictObject({
  account: accountSchema,
  players: z.array(
    z.strictObject({ galaxyId: idSchema, playerId: idSchema, displayName: z.string() })
  ),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
